import './styles/main.css';
import { Editor } from './components/Editor';
import { TabManager } from './components/TabManager';
import { Sidebar } from './components/Sidebar';
import { MarkdownPreview } from './components/MarkdownPreview';
import { TableOfContents } from './components/TableOfContents';
import { JsonPanel } from './components/JsonPanel';
import { KnowledgeSearch } from './components/KnowledgeSearch';
import { ConnectionsDiscovery } from './components/ConnectionsDiscovery';
import { KnowledgeQA } from './components/KnowledgeQA';
import { MindMapView } from './components/MindMapView';
import { InboxView } from './components/InboxView';
import { ProjectsView } from './components/ProjectsView';
import { KnowledgeBaseView } from './components/KnowledgeBaseView';
import { OutreachView } from './components/OutreachView';
import { PatternsView } from './components/PatternsView';
import { Settings } from './components/Settings';
import { SettingsView } from './components/SettingsView';
import { ProfilesView } from './components/ProfilesView';
import { VaultView } from './components/VaultView';
import { AudienceView } from './components/AudienceView';
import { ReviewWorkflowView } from './components/ReviewWorkflowView';
import { ClassificationQueueView } from './components/ClassificationQueueView';
import { StatsDashboardView } from './components/StatsDashboardView';
import { HelpModal } from './components/HelpModal';
import { IngestModal } from './components/IngestModal';
import { api, type Document } from './services/api';
import { resourcesApi } from './services/resources-api';
import { storage } from './services/storage';
import { syncService, type SyncEvent } from './services/sync';
import { fileImportService, type ImportedFile } from './services/file-import';
import { ChromeAIDetector } from './services/chrome-ai-detector';
import { HybridSummarizer } from './services/hybrid-summarizer';
import { HybridTagger } from './services/hybrid-tagger';
import { HybridClassifier } from './services/hybrid-classifier';
import { gdriveService, type GDriveFile } from './services/gdrive';
import { offlineService } from './services/offline';
import { SERVER_AUTOSAVE_INTERVAL_MS, API_BASE_URL, BUILD_VERSION } from './config/settings';
import { marked } from 'marked';
import { FIELD_CONFIG, type FieldDefinition } from './config/field-config';
import { AIActionsController } from './components/AIActionsController';
import { ReviewModeManager } from './components/ReviewModeManager';
import { TranscriptionController } from './components/TranscriptionController';
import { ImportManager } from './components/ImportManager';
import { ViewManager } from './components/ViewManager';
import { PanelController } from './components/PanelController';
import { DocumentInfoPanelManager } from './components/DocumentInfoPanelManager';
import { DocumentSyncService } from './services/document-sync';

// Local types (migrated from deleted legacy APIs)
interface KnowledgeItemDetail {
  id: string;
  title: string;
  source: string;
  content?: string;
  excerpt?: string;
  url?: string;
  categories?: string[];
  projectId?: string;
  updatedAt?: number;
}

class App {
  private editor: Editor;
  private tabManager: TabManager;
  private sidebar: Sidebar;
  private preview: MarkdownPreview;
  private toc: TableOfContents;
  private jsonPanel: JsonPanel;
  private knowledgeSearch: KnowledgeSearch;
  private connectionsDiscovery: ConnectionsDiscovery;
  private knowledgeQA: KnowledgeQA;
  private mindMapView: MindMapView;
  private inboxView: InboxView;
  private projectsView: ProjectsView;
  private knowledgeBaseView: KnowledgeBaseView;
  private outreachView: OutreachView;
  private patternsView: PatternsView;
  private settings: Settings;
  private settingsView: SettingsView;
  private profilesView: ProfilesView;
  private vaultView: VaultView;
  private audienceView: AudienceView;
  private reviewWorkflowView: ReviewWorkflowView;
  private classificationQueueView: ClassificationQueueView;
  private statsDashboardView: StatsDashboardView;
  private helpModal: HelpModal;
  private aiActionsController!: AIActionsController;
  private reviewModeManager!: ReviewModeManager;
  private transcriptionController!: TranscriptionController;
  private importManager!: ImportManager;
  private viewManager!: ViewManager;
  private panelController!: PanelController;
  private docInfoPanelManager!: DocumentInfoPanelManager;
  private docSyncService!: DocumentSyncService;
  private currentDoc: Document | null = null;
  private statusEl: HTMLElement;
  private docInfoEl: HTMLElement;
  private tagsEditorEl: HTMLElement;
  private tagsListEl: HTMLElement;
  private tagInputEl: HTMLInputElement;
  private offlineIndicator: HTMLElement;
  private isViewingKnowledgeItem: boolean = false;

  // Current view managed by ViewManager
  private get currentView(): string { return this.viewManager?.getCurrentView() || 'notes'; }

  constructor() {
    this.editor = new Editor('editor');
    this.tabManager = new TabManager('tabs-list');
    this.sidebar = new Sidebar();
    this.preview = new MarkdownPreview('preview');
    this.toc = new TableOfContents('toc-panel');
    this.jsonPanel = new JsonPanel('json-panel');
    this.knowledgeSearch = new KnowledgeSearch();
    this.connectionsDiscovery = new ConnectionsDiscovery();
    this.knowledgeQA = new KnowledgeQA();
    this.mindMapView = new MindMapView();
    this.inboxView = new InboxView();
    this.projectsView = new ProjectsView();
    this.knowledgeBaseView = new KnowledgeBaseView();
    this.outreachView = new OutreachView();
    this.patternsView = new PatternsView();
    this.settings = new Settings();
    this.settingsView = new SettingsView();
    this.profilesView = new ProfilesView();
    this.vaultView = new VaultView();
    this.audienceView = new AudienceView();
    this.reviewWorkflowView = new ReviewWorkflowView();
    this.classificationQueueView = new ClassificationQueueView();
    this.statsDashboardView = new StatsDashboardView();
    this.helpModal = new HelpModal();
    
    // AI Actions Controller
    this.aiActionsController = new AIActionsController({
      saveFieldValue: (field, value, skipRender) => this.docInfoPanelManager.saveFieldValue(field, value, skipRender),
      renderDocInfoFields: () => this.docInfoPanelManager.renderFields(),
      getCurrentDoc: () => this.currentDoc,
      getKnowledgePreviewDoc: () => this.knowledgeBaseView.getCurrentPreviewDoc()
    });

    // Review Mode Manager
    this.reviewModeManager = new ReviewModeManager();

    // Transcription Controller
    this.transcriptionController = new TranscriptionController({
      getCurrentDoc: () => this.currentDoc,
      setCurrentDoc: (doc) => { this.currentDoc = doc; },
      showNotification: (msg, type) => this.showNotification(msg, type)
    });

    // Import Manager
    this.importManager = new ImportManager({
      onImportSuccess: async (docId, title) => {
        this.sidebar.refresh();
        await this.sidebar.loadTags();
        this.tabManager.openTab(docId, title);
      },
      onImportError: (error) => alert(error)
    });

    // View Manager
    this.viewManager = new ViewManager({
      loadView: async (view) => {
        if (view === 'inbox') await this.inboxView.load();
        else if (view === 'knowledge') await this.knowledgeBaseView.load();
        else if (view === 'outreach') await this.outreachView.load();
        else if (view === 'patterns') await this.patternsView.loadPatterns();
        else if (view === 'profiles') await this.profilesView.load();
        else if (view === 'settings') await this.settingsView.init();
        else if (view === 'vault') await this.vaultView.load();
        else if (view === 'audience') await this.audienceView.load();
        else if (view === 'review') await this.reviewWorkflowView.load();
        else if (view === 'classification') await this.classificationQueueView.load();
        else if (view === 'stats') await this.statsDashboardView.load();
      },
      syncSidebarTab: (view) => this.sidebar.switchTab(view, true),  // silent=true to prevent callback loop
      getActiveTabId: () => this.tabManager.getActiveTabId(),
      getActiveTab: () => this.sidebar.getActiveTab(),
      updateAIVisibility: () => this.updateAIActionsVisibility(),
      getSelectedId: (view) => view === 'knowledge' ? this.knowledgeBaseView?.getSelectedDocId() : undefined
    });

    // Panel Controller
    this.panelController = new PanelController(
      this.preview,
      this.toc,
      this.jsonPanel,
      {
        getEditorContent: () => this.editor.getContent(),
        setEditorContent: (content) => this.editor.setContent(content),
        scrollEditorToLine: (line) => this.editor.scrollToLine(line),
        hasCurrentDoc: () => this.currentDoc !== null,
        onEditorChange: (content) => this.handleEditorChange(content),
        onPreviewEnabled: () => {
          if (this.currentDoc && !this.docInfoPanelManager.isVisibleForDoc(this.currentDoc.meta.id)) {
            this.docInfoPanelManager.addVisibleDoc(this.currentDoc.meta.id);
            this.docInfoPanelManager.showPanel();
            this.docInfoPanelManager.loadVisibleFields();
            this.docInfoPanelManager.setup();
            this.docInfoPanelManager.renderFields();
          }
        }
      }
    );

    // Document Info Panel Manager
    this.docInfoPanelManager = new DocumentInfoPanelManager(
      {
        getCurrentDoc: () => this.currentDoc,
        setCurrentDoc: (doc) => { this.currentDoc = doc; },
        getActiveTabId: () => this.tabManager.getActiveTabId(),
        addTag: (tag) => this.addTag(tag),
        removeTag: (tag) => this.removeTag(tag),
        onDelete: async (docId) => {
          const response = await api.deleteDocument(docId);
          if (response.success) {
            this.tabManager.closeTab(docId, true);
            await this.sidebar.loadDocuments();
          } else {
            alert('Failed to delete document');
          }
        },
        toggleKnowledge: (docId, enabled) => this.toggleKnowledgeForDoc(docId, enabled)
      },
      this.aiActionsController,
      this.transcriptionController,
      this.sidebar,
      this.tabManager
    );

    // Document Sync Service - centralizes update + sync patterns
    this.docSyncService = new DocumentSyncService(this.sidebar, {
      getCurrentDoc: () => this.currentDoc,
      setCurrentDoc: (doc) => { this.currentDoc = doc; },
      updateCurrentDocMeta: (updates) => {
        if (this.currentDoc) {
          this.currentDoc.meta = { ...this.currentDoc.meta, ...updates };
        }
      }
    });

    this.statusEl = document.getElementById('sync-status')!;
    this.docInfoEl = document.getElementById('doc-info')!;
    this.tagsEditorEl = document.getElementById('tags-editor')!;
    this.tagsListEl = document.getElementById('tags-list-editor')!;
    this.tagInputEl = document.getElementById('tag-input') as HTMLInputElement;
    this.offlineIndicator = document.getElementById('offline-indicator')!;


    // Show build version
    const versionEl = document.getElementById('version-indicator');
    if (versionEl) {
      versionEl.textContent = `v${BUILD_VERSION}`;
      versionEl.title = `Build: ${BUILD_VERSION}`;
    }
    const sidebarVersionEl = document.getElementById('sidebar-version');
    if (sidebarVersionEl) {
      sidebarVersionEl.textContent = `v${BUILD_VERSION}`;
    }

    this.init();
  }

  private async init(): Promise<void> {
    this.setupEventListeners();
    this.setupSync();
    this.setupFileImport();
    this.setupTagsEditor();
    this.setupServerAutosave();
    this.setupOffline();
    this.setupToolbar();
    this.setupKnowledgeSearch();
    this.viewManager.setupBackNavigation();

    // Apply initial panel states
    this.panelController.applyInitialState();

    // Cargar datos iniciales
    await this.sidebar.refresh();

    // Check URL params FIRST before default tab loading
    const urlHandled = await this.restoreStateFromUrl();

    // Only load default tabs if URL didn't specify a view/doc
    if (!urlHandled) {
      // Renderizar tabs y abrir el activo si existe
      this.tabManager.render();
      this.updateSidebarActiveDocs();
      const activeTabId = this.tabManager.getActiveTabId();

      // Safety check: ensure the active tab actually exists in the tabs list
      if (activeTabId && this.tabManager.hasTab(activeTabId)) {
        await this.loadDocument(activeTabId);
      } else if (activeTabId && !this.tabManager.hasTab(activeTabId)) {
        // Stale activeTabId - clear it and show empty state
        storage.clearActiveTabId();
        this.showEmptyState();
      } else {
        this.showEmptyState();
      }
    }
  }

  /**
   * Update URL with current view and document
   */

  /**
   * Restore view and document from URL on page load
   * Returns true if URL specified a view or document
   */
  private async restoreStateFromUrl(): Promise<boolean> {
    const url = new URL(window.location.href);
    const view = url.searchParams.get('view');
    const docId = url.searchParams.get('doc');
    const resourceId = url.searchParams.get('resource');

    let handled = false;

    // Restore view first
    if (view && ['inbox', 'knowledge', 'outreach', 'patterns', 'profiles', 'settings', 'vault'].includes(view)) {
      await this.viewManager.switchToView(view);
      handled = true;
    }

    // Open resource if specified (from Knowledge Base URL)
    if (resourceId) {
      try {
        await this.viewManager.switchToView('knowledge');
        await this.knowledgeBaseView.load();
        await this.knowledgeBaseView.openResource(resourceId);
        handled = true;
      } catch (error) {
        console.warn('Resource from URL not found:', resourceId);
        // Clear invalid resource from URL
        const cleanUrl = new URL(window.location.href);
        cleanUrl.searchParams.delete('resource');
        window.history.replaceState({}, '', cleanUrl.toString());
      }
    }

    // Then open document if specified
    if (docId) {
      try {
        // Check if document exists
        const response = await api.getDocument(docId);
        if (response.success && response.data) {
          // Switch to notes view and open the document
          await this.viewManager.switchToView('notes');
          this.tabManager.render();
          if (!this.tabManager.hasTab(docId)) {
            this.tabManager.openTab(docId, response.data.meta.title || 'Untitled');
          } else {
            this.tabManager.selectTab(docId);
          }
          this.updateSidebarActiveDocs();
          handled = true;
        }
      } catch (error) {
        console.warn('Document from URL not found:', docId);
        // Clear invalid doc from URL
        this.viewManager.updateUrl(this.currentView);
      }
    }

    return handled;
  }

  private setupEventListeners(): void {
    // Toggle sidebar collapsed
    document.getElementById('sidebar-toggle')?.addEventListener('click', () => {
      const sidebar = document.getElementById('sidebar');
      if (sidebar) {
        sidebar.classList.toggle('collapsed');
        localStorage.setItem('sidebar_collapsed', sidebar.classList.contains('collapsed') ? 'true' : 'false');
      }
    });

    // Restore sidebar state
    const sidebarCollapsed = localStorage.getItem('sidebar_collapsed') === 'true';
    if (sidebarCollapsed) {
      document.getElementById('sidebar')?.classList.add('collapsed');
    }

    // Nuevo documento
    document.getElementById('btn-new-doc')?.addEventListener('click', () => {
      this.createNewDocument();
    });

    // Nueva carpeta
    document.getElementById('btn-new-folder')?.addEventListener('click', () => {
      this.createNewFolder();
    });

    // Importar archivo
    document.getElementById('btn-import')?.addEventListener('click', async () => {
      const file = await fileImportService.openFile();
      if (file) {
        await this.importManager.importFile(file);
      }
    });

    // Google Drive
    document.getElementById('btn-gdrive')?.addEventListener('click', async () => {
      await this.importManager.handleGoogleDrive();
    });

    // Tab selection - switch to editor view when selecting a document tab
    this.tabManager.onTabSelect(async (tabId) => {
      await this.viewManager.switchToView('notes');
      await this.loadDocument(tabId);
      this.updateSidebarActiveDocs();
    });

    // Tab close (solo cierra la pestaña, NO elimina el documento)
    this.tabManager.onTabClose((tabId) => {
      const tab = this.tabManager.getTabs().find(t => t.id === tabId);
      if (tab?.hasUnsavedChanges) {
        return confirm('¿Cerrar sin guardar los cambios?');
      }
      return true;
    });

    // Cuando se cierra un tab y no quedan más, mostrar empty state
    this.tabManager.onTabClosed(() => {
      this.updateSidebarActiveDocs();
      if (this.tabManager.getTabs().length === 0) {
        this.currentDoc = null;
        this.tagsEditorEl.style.display = 'none';
        this.showEmptyState();
        // Clear doc from URL when no tabs open
        this.viewManager.updateUrl(this.currentView);
        // Hide AI actions when no document open
        this.updateAIActionsVisibility();
      } else {
        // Update URL with new active tab
        const activeTabId = this.tabManager.getActiveTabId();
        if (activeTabId) {
          this.viewManager.updateUrl(this.currentView, activeTabId);
        }
      }
    });

    // Tab rename (doble clic en título del tab)
    this.tabManager.onTabRename(async (tabId, newTitle) => {
      await this.renameDocument(tabId, newTitle);
    });

    // Tab info click - toggle info panel
    this.tabManager.onTabInfo((tabId) => {
      this.docInfoPanelManager.toggle(tabId);
    });

    // Document selection from sidebar
    this.sidebar.onDocumentSelect(async (doc) => {
      // Always switch to notes view first (in case we're in another view)
      await this.viewManager.switchToView('notes');

      if (this.tabManager.hasTab(doc.id)) {
        this.tabManager.selectTab(doc.id);
        // selectTab ya llama a onSelect -> loadDocument
      } else {
        this.tabManager.openTab(doc.id, doc.title);
        // openTab ya llama a onSelect -> loadDocument, no hace falta llamarlo de nuevo
      }
    });

    // Inbox item click - open document or resource
    this.inboxView.setOnItemClick(async (itemId) => {
      if (itemId === 'new') {
        await this.createNewDocument();
      } else {
        // Push current state for back navigation
        this.viewManager.pushNavigationState();

        // Check if it's a resource (InboxView marks resources with _isResource)
        const inboxItem = this.inboxView.getItemById(itemId);

        if (inboxItem && (inboxItem as unknown as { _isResource?: boolean })._isResource) {
          // Open resource in Knowledge Base view
          await this.viewManager.switchToView('knowledge');
          await this.knowledgeBaseView.openResource(itemId);
        } else {
          // Open document in editor
          await this.viewManager.switchToView('notes');
          if (this.tabManager.hasTab(itemId)) {
            this.tabManager.selectTab(itemId);
          } else {
            const doc = await api.getDocument(itemId);
            if (doc.success && doc.data) {
              this.tabManager.openTab(itemId, doc.data.meta.title);
            }
          }
        }
      }
    });

    // Document deletion from sidebar
    this.sidebar.onDocumentDelete(async (docId) => {
      await this.deleteDocument(docId);
    });

    // Document rename from sidebar (doble clic)
    this.sidebar.onDocumentRename(async (docId, newTitle) => {
      await this.renameDocument(docId, newTitle);
    });

    // Document favorite toggle
    this.sidebar.onDocumentFavorite(async (docId, isFavorite) => {
      await this.toggleFavorite(docId, isFavorite);
    });

    // Document move to folder
    this.sidebar.onDocumentMove(async (docId, folderId) => {
      await this.moveDocumentToFolder(docId, folderId);
    });

    // Document archive
    this.sidebar.onDocumentArchive(async (docId, isArchived) => {
      await this.toggleArchive(docId, isArchived);
    });

    // Project selection from sidebar
    this.sidebar.onProjectSelectCallback(async (projectName) => {
      await this.viewManager.switchToView('notes'); // Projects view is part of notes for now
      const allDocs = this.sidebar.getDocuments();
      this.projectsView.show(projectName, allDocs);
    });

    // Resource category selection from sidebar
    this.sidebar.onResourceCategorySelectCallback(async (category) => {
      await this.viewManager.switchToView('knowledge');
      this.knowledgeBaseView.filterByCategory(category);
    });

    // Intent filter from sidebar quick filters (Read Later, Apply Later)
    this.sidebar.onIntentFilterCallback((intent) => {
      this.knowledgeBaseView.setIntentFilter(intent);
    });

    // Document selection from projects view
    this.projectsView.onDocumentSelect(async (doc) => {
      await this.viewManager.switchToView('notes');
      this.tabManager.openTab(doc.id, doc.title || 'Untitled');
    });

    // Knowledge Base view callbacks
    this.knowledgeBaseView.onResourceSelectCallback(async (doc, fromReviewMode) => {
      // Push current state for back navigation
      this.viewManager.pushNavigationState();
      this.docInfoPanelManager.setOpenedFromReview(fromReviewMode || false);
      await this.viewManager.switchToView('notes');
      this.tabManager.openTab(doc.id, doc.title || 'Untitled');
    });

    this.knowledgeBaseView.onResourceCreateCallback(() => {
      const modal = new IngestModal();
      modal.onIngestSuccess(() => {
        this.knowledgeBaseView.refresh();
      });
      modal.show();
    });

    // Sidebar Add Resource button - opens IngestModal
    document.getElementById('btn-add-resource')?.addEventListener('click', () => {
      const modal = new IngestModal();
      modal.onIngestSuccess(() => {
        this.knowledgeBaseView.refresh();
      });
      modal.show();
    });

    // Create Document button - creates new document
    document.getElementById('btn-create-document')?.addEventListener('click', () => {
      this.createNewDocument();
    });

    // Toggle archived documents visibility
    document.getElementById('btn-archived')?.addEventListener('click', () => {
      const isShowing = this.sidebar.toggleShowArchived();
      const btn = document.getElementById('btn-archived');
      if (btn) {
        btn.classList.toggle('active', isShowing);
        btn.title = isShowing ? 'Ocultar archivados' : 'Mostrar archivados';
      }
    });

    // Editor changes
    this.editor.onContentChange((content) => {
      this.handleEditorChange(content);
    });

    // Cursor changes - sync scroll con preview
    this.editor.onCursorChangeCallback((line) => {
      if (this.panelController.isPreviewEnabled() && this.currentDoc) {
        const totalLines = this.editor.getContent().split('\n').length;
        this.preview.scrollToLine(line, totalLines);
      }
    });

    // Keyboard shortcuts
    document.addEventListener('keydown', (e) => {
      // Ctrl/Cmd + S - Save
      if ((e.ctrlKey || e.metaKey) && e.key === 's') {
        e.preventDefault();
        this.saveCurrentDocument();
      }

      // Ctrl/Cmd + N - New document
      if ((e.ctrlKey || e.metaKey) && e.key === 'n') {
        e.preventDefault();
        this.createNewDocument();
      }

      // Ctrl/Cmd + O - Open file
      if ((e.ctrlKey || e.metaKey) && e.key === 'o') {
        e.preventDefault();
        fileImportService.openFile().then(file => {
          if (file) this.importManager.importFile(file);
        });
      }
    });

    // Prevent closing with unsaved changes + guardar pendientes
    window.addEventListener('beforeunload', (e) => {
      if (this.tabManager.hasUnsavedChanges()) {
        // Intentar guardar antes de cerrar
        this.savePendingChangesToServerSync();
        e.preventDefault();
        e.returnValue = '';
      }
    });
  }

  private setupSync(): void {
    syncService.onSync((event: SyncEvent) => {
      switch (event.type) {
        case 'document_changed':
          if (event.documentId === this.currentDoc?.meta.id) {
            this.showSyncNotification(event.documentId!);
          }
          break;

        case 'sync_complete':
          this.statusEl.textContent = 'Sincronizado';
          this.statusEl.className = 'synced';
          break;

        case 'sync_error':
          this.statusEl.textContent = 'Error de sincronización';
          this.statusEl.className = 'error';
          break;
      }
    });

    // syncService.start(); // Disabled - no multi-device sync polling
  }

  private setupFileImport(): void {
    this.importManager.initDropZone();
  }

  private setupTagsEditor(): void {
    // Añadir tag al presionar Enter
    this.tagInputEl.addEventListener('keydown', async (e) => {
      if (e.key === 'Enter') {
        e.preventDefault();
        const tag = this.tagInputEl.value.trim().toLowerCase();
        if (tag && this.currentDoc) {
          await this.addTag(tag);
          this.tagInputEl.value = '';
        }
      }
    });
  }

  private setupServerAutosave(): void {
    // Guardar en servidor cada 60 segundos (solo si hay cambios)
    setInterval(() => {
      this.savePendingChangesToServer();
    }, SERVER_AUTOSAVE_INTERVAL_MS);
  }

  private async savePendingChangesToServer(): Promise<void> {
    // Buscar todos los documentos con cambios pendientes
    const tabs = this.tabManager.getTabs();

    for (const tab of tabs) {
      if (!tab.hasUnsavedChanges) continue;

      const draft = storage.getDraft(tab.id);
      if (!draft) continue;

      // Obtener título del draft
      const firstLine = draft.split('\n')[0].replace(/^#*\s*/, '').trim();
      const title = firstLine || tab.title;

      this.statusEl.textContent = 'Guardando...';
      this.statusEl.className = 'syncing';

      const response = await api.updateDocument(tab.id, { content: draft, title });

      const synced = await this.docSyncService.sync(tab.id, response, {
        manageStorage: true,
        tabManager: this.tabManager
      });

      this.statusEl.textContent = synced ? 'Guardado' : 'Error al guardar';
      this.statusEl.className = synced ? 'synced' : 'error';
    }
  }

  private savePendingChangesToServerSync(): void {
    // Versión síncrona para beforeunload (usa sendBeacon si es posible)
    const tabs = this.tabManager.getTabs();

    for (const tab of tabs) {
      if (!tab.hasUnsavedChanges) continue;

      const draft = storage.getDraft(tab.id);
      if (!draft) continue;

      const firstLine = draft.split('\n')[0].replace(/^#*\s*/, '').trim();
      const title = firstLine || tab.title;

      // Intentar guardar con sendBeacon (no bloquea cierre)
      const url = `https://notepad-custom.thesocialaudience.workers.dev/api/docs/${tab.id}`;
      const data = JSON.stringify({ content: draft, title });

      navigator.sendBeacon(url, new Blob([data], { type: 'application/json' }));
    }
  }

  private renderTags(): void {
    if (!this.currentDoc) {
      this.tagsEditorEl.style.display = 'none';
      return;
    }

    this.tagsEditorEl.style.display = 'flex';
    this.tagsListEl.innerHTML = '';

    for (const tag of this.currentDoc.meta.tags) {
      const tagEl = document.createElement('span');
      tagEl.className = 'tag-editable';
      tagEl.innerHTML = `
        ${tag}
        <button class="tag-remove" data-tag="${tag}">&times;</button>
      `;
      this.tagsListEl.appendChild(tagEl);
    }

    // Event listeners para eliminar tags
    this.tagsListEl.querySelectorAll('.tag-remove').forEach(btn => {
      btn.addEventListener('click', async () => {
        const tag = btn.getAttribute('data-tag')!;
        await this.removeTag(tag);
      });
    });
  }

  private async addTag(tag: string): Promise<void> {
    if (!this.currentDoc || this.currentDoc.meta.tags.includes(tag)) return;

    const newTags = [...this.currentDoc.meta.tags, tag];
    await this.updateTags(newTags);
  }

  private async removeTag(tag: string): Promise<void> {
    if (!this.currentDoc) return;

    const newTags = this.currentDoc.meta.tags.filter(t => t !== tag);
    await this.updateTags(newTags);
  }

  private async updateTags(tags: string[]): Promise<void> {
    if (!this.currentDoc) return;

    const response = await api.updateDocument(this.currentDoc.meta.id, { tags });
    await this.docSyncService.sync(this.currentDoc.meta.id, response, {
      onSuccess: async () => {
        this.renderTags();
        await this.sidebar.loadTags();
      }
    });
  }

  private async renameDocument(docId: string, newTitle: string): Promise<void> {
    const response = await api.updateDocument(docId, { title: newTitle });

    const synced = await this.docSyncService.sync(docId, response, {
      onSuccess: () => {
        if (this.tabManager.hasTab(docId)) {
          this.tabManager.updateTabTitle(docId, newTitle);
        }
      }
    });

    if (!synced) {
      alert('Error al renombrar el documento');
    }
  }

  private async deleteDocument(docId: string): Promise<void> {
    if (!confirm('¿Eliminar este documento? Esta acción no se puede deshacer.')) {
      return;
    }

    const response = await api.deleteDocument(docId);

    if (response.success) {
      // Cerrar tab si está abierta (force=true para no preguntar)
      if (this.tabManager.hasTab(docId)) {
        this.tabManager.closeTab(docId, true);
      }

      // Eliminar de la sidebar
      this.sidebar.removeDocument(docId);

      // Si era el documento actual, limpiar
      if (this.currentDoc?.meta.id === docId) {
        this.currentDoc = null;
        this.tagsEditorEl.style.display = 'none';

        // Mostrar empty state si no hay tabs
        if (this.tabManager.getTabs().length === 0) {
          this.showEmptyState();
        }
      }
    } else {
      alert('Error al eliminar el documento');
    }
  }

  private showSyncNotification(docId: string): void {
    if (confirm('Este documento ha sido modificado en otro dispositivo. ¿Recargar?')) {
      this.loadDocument(docId);
    }
  }

  private async loadDocument(id: string): Promise<void> {
    // Skip if viewing a knowledge item (content already loaded)
    if (this.isViewingKnowledgeItem) {
      this.isViewingKnowledgeItem = false;
      return;
    }

    // Skip if this is a knowledge item ID (not a notepad document)
    if (id.includes(':') || id.startsWith('knowledge_') || id.startsWith('multimedia_')) {
      return;
    }

    const response = await api.getDocument(id);

    if (!response.success || !response.data) {
      alert('Error al cargar el documento');
      return;
    }

    this.currentDoc = response.data;
    storage.setLocalVersion(id, response.data.meta.version);
    storage.setCachedDocument(id, response.data);

    // Restaurar el contenedor del editor si estaba en empty state
    const container = document.getElementById('editor-container')!;
    if (!document.getElementById('editor')) {
      container.innerHTML = '<div id="editor"></div>';
      this.editor = new Editor('editor');
      this.editor.onContentChange((content) => {
        this.handleEditorChange(content);
      });
      this.editor.onCursorChangeCallback((line) => {
        if (this.panelController.isPreviewEnabled() && this.currentDoc) {
          const totalLines = this.editor.getContent().split('\n').length;
          this.preview.scrollToLine(line, totalLines);
        }
      });
    }

    // Check for local draft
    const draft = storage.getDraft(id);
    const content = draft ?? response.data.content;
    const fileType = response.data.meta.fileType || 'markdown';

    this.editor.init(content, id, fileType);
    this.sidebar.setActiveDoc(id);
    this.updateStatusBar();
    this.renderTags();
    this.updateFavoriteButton();

    // Update panels
    this.panelController.updatePanels(content);

    // Dar foco al editor para permitir edición inmediata
    setTimeout(() => {
      this.editor.focus();
    }, 50);

    if (draft) {
      this.tabManager.setUnsaved(id, true);
    }

    // Update info panel visibility for this document
    this.docInfoPanelManager.updateForDoc(id);

    // Update transcribe button status
    this.transcriptionController.refreshButton();

    // Update AI actions visibility
    this.updateAIActionsVisibility();

    // Update URL with document ID (for shareable links)
    this.viewManager.updateUrl(this.currentView, id);
  }

  private async createNewDocument(): Promise<void> {
    const response = await api.createDocumentWithUniqueTitle({
      title: 'New Document',
      content: ''
    });

    if (!response.success || !response.data) {
      alert('Error al crear el documento');
      return;
    }

    const doc = response.data;
    this.sidebar.addDocument(doc.meta);

    // Restaurar el contenedor del editor si estaba en empty state (antes de openTab)
    const container = document.getElementById('editor-container')!;
    if (!document.getElementById('editor')) {
      container.innerHTML = '<div id="editor"></div>';
      this.editor = new Editor('editor');
      this.editor.onContentChange((content) => {
        this.handleEditorChange(content);
      });
      this.editor.onCursorChangeCallback((line) => {
        if (this.panelController.isPreviewEnabled() && this.currentDoc) {
          const totalLines = this.editor.getContent().split('\n').length;
          this.preview.scrollToLine(line, totalLines);
        }
      });
    }

    this.tabManager.openTab(doc.meta.id, doc.meta.title);
    // openTab ya llama a onSelect -> loadDocument

    // Dar tiempo al editor a inicializarse y dar foco
    setTimeout(() => {
      this.editor.focus();
    }, 100);
  }


  private handleEditorChange(content: string): void {
    if (!this.currentDoc) return;

    const id = this.currentDoc.meta.id;

    // Guardar inmediatamente en localStorage (navegador)
    storage.setDraft(id, content);
    this.tabManager.setUnsaved(id, true);

    // Auto-detect title from first line
    const firstLine = content.split('\n')[0].replace(/^#*\s*/, '').trim();
    if (firstLine && firstLine !== this.currentDoc.meta.title) {
      this.tabManager.updateTabTitle(id, firstLine || 'Sin título');
    }

    this.statusEl.textContent = 'En memoria';
    this.statusEl.className = '';
    this.updateStatusBar();

    // Update panels
    this.panelController.updatePanels(content);
  }

  private async saveCurrentDocument(): Promise<void> {
    if (!this.currentDoc) return;

    const content = this.editor.getContent();
    const firstLine = content.split('\n')[0].replace(/^#*\s*/, '').trim();

    this.statusEl.textContent = 'Guardando...';
    this.statusEl.className = 'syncing';

    const response = await api.updateDocument(this.currentDoc.meta.id, {
      content,
      title: firstLine || this.currentDoc.meta.title
    });

    const synced = await this.docSyncService.sync(this.currentDoc.meta.id, response, {
      manageStorage: true,
      tabManager: this.tabManager
    });

    this.statusEl.textContent = synced ? 'Guardado' : 'Error al guardar';
    this.statusEl.className = synced ? 'synced' : 'error';
  }

  private updateStatusBar(): void {
    if (!this.currentDoc) {
      this.docInfoEl.textContent = '';
      return;
    }

    const cursorInfo = this.editor.getCursorInfo();
    const wordCount = this.editor.getWordCount();

    this.docInfoEl.textContent = `Ln ${cursorInfo.line}, Col ${cursorInfo.column} | ${wordCount} palabras`;
  }

  private updateSidebarActiveDocs(): void {
    const tabs = this.tabManager.getTabs();
    this.sidebar.renderActiveWorkspaces(tabs);
  }

  private showEmptyState(): void {
    const container = document.getElementById('editor-container')!;
    container.innerHTML = `
      <div class="empty-state">
        <h2>Bienvenido a Notepad</h2>
        <p>Crea un nuevo documento o abre uno existente desde la barra lateral</p>
      </div>
    `;

    // Hide related docs
    this.sidebar.hideRelatedDocs();

    // Clear active doc in sidebar (hides Resources from Documents list)
    this.sidebar.setActiveDoc(null);

    // Hide info panel when no doc is open
    this.docInfoPanelManager.hidePanel();
  }




  // ===== NUEVAS FUNCIONALIDADES =====

  private setupOffline(): void {
    offlineService.init();

    offlineService.onStatusChange((isOnline) => {
      if (isOnline) {
        this.offlineIndicator.classList.add('hidden');
        this.statusEl.textContent = 'Reconectado';
        this.statusEl.className = 'synced';
      } else {
        this.offlineIndicator.classList.remove('hidden');
        this.statusEl.textContent = 'Sin conexión';
        this.statusEl.className = 'error';
      }
    });
  }

  private setupToolbar(): void {
    // Botón buscar (en el editor)
    document.getElementById('btn-search')?.addEventListener('click', () => {
      // Focus editor first, then open search
      this.editor.focus();
      setTimeout(() => {
        this.editor.openSearch();
      }, 50);
    });

    // Botón preview
    document.getElementById('btn-preview')?.addEventListener('click', () => {
      this.panelController.togglePreview();
    });

    // Botón favorite (usa btn-bookmark)
    document.getElementById('btn-bookmark')?.addEventListener('click', () => {
      if (this.currentDoc) {
        this.toggleFavorite(this.currentDoc.meta.id, !this.currentDoc.meta.isFavorite);
      }
    });

    // Botón Review Mode
    document.getElementById('btn-review-mode')?.addEventListener('click', () => {
      this.reviewModeManager.toggle();
    });

    // Botón TOC
    document.getElementById('btn-toc')?.addEventListener('click', () => {
      this.panelController.toggleToc();
    });

    // Botón JSON Tools
    document.getElementById('btn-json')?.addEventListener('click', () => {
      this.panelController.toggleJsonPanel();
    });

    // Botón exportar
    document.getElementById('btn-export')?.addEventListener('click', () => {
      this.exportAsHtml();
    });

    // App Settings (sidebar footer) - open as tab, not modal
    document.getElementById('btn-settings')?.addEventListener('click', () => {
      this.viewManager.switchToView('settings');
    });

    document.getElementById('btn-discovery')?.addEventListener('click', () => {
      this.connectionsDiscovery.open();
    });

    document.getElementById('btn-export-all')?.addEventListener('click', () => {
      this.exportAllDocuments();
    });

    // Vault button in filter-bar-right
    document.getElementById('btn-vault')?.addEventListener('click', () => {
      this.viewManager.switchToView('vault');
    });

    // Audience button in filter-bar-right
    document.getElementById('btn-audience')?.addEventListener('click', () => {
      this.viewManager.switchToView('audience');
    });

    // Help button in filter-bar-right
    document.getElementById('btn-help')?.addEventListener('click', () => {
      this.helpModal.open();
    });

    // AI Buttons in Filter Bar
    document.getElementById('btn-ai-summarize')?.addEventListener('click', () => {
      this.aiActionsController.handleSummarizeButton();
    });

    document.getElementById('btn-ai-tags')?.addEventListener('click', () => {
      this.aiActionsController.handleExtractTagsButton();
    });

    document.getElementById('btn-ai-classify')?.addEventListener('click', () => {
      this.aiActionsController.handleClassifyButton();
    });

    // Sidebar tab change handler
    this.sidebar.onTabChangeCallback((tab) => {
      this.handleSidebarTabChange(tab);
    });

    // Document Knowledge toggle from context menu
    this.sidebar.onDocumentKnowledge(async (docId, enabled) => {
      await this.toggleKnowledgeForDoc(docId, enabled);
    });

    // Document Workspace change from context menu
    this.sidebar.onDocumentWorkspace(async (docId, workspaceId) => {
      await this.moveDocumentToWorkspace(docId, workspaceId);
    });

    // Workspace change from selector
    this.sidebar.onWorkspaceChangeCallback(async (workspaceId) => {
      await this.handleWorkspaceChange(workspaceId);
    });

    // Knowledge item open (from Resources/Media tabs or Related panel)
    this.sidebar.onKnowledgeItemOpenCallback((item) => {
      this.openKnowledgeItem(item);
    });

    // Knowledge preview state change - update AI button visibility
    this.knowledgeBaseView.onPreviewChange(() => {
      this.updateAIActionsVisibility();
    });
  }

  private async handleWorkspaceChange(workspaceId: string): Promise<void> {
    // Switch workspace - tabs are preserved per workspace
    this.tabManager.setWorkspace(workspaceId);

    // Load active tab for this workspace, or show empty state
    const activeTabId = this.tabManager.getActiveTabId();
    if (activeTabId) {
      await this.loadDocument(activeTabId);
    } else {
      this.currentDoc = null;
      this.showEmptyState();
    }
  }

  private openKnowledgeItem(item: KnowledgeItemDetail): void {
    // Push current state BEFORE switching (for back navigation)
    this.viewManager.pushNavigationState();

    // Show editor and hide other views
    const editorContainer = document.getElementById('editor-container');
    if (editorContainer) editorContainer.classList.remove('hidden');

    // Clear current doc (this is not a notepad document)
    this.currentDoc = null;
    this.tagsEditorEl.style.display = 'none';

    // Set content in editor (read-only mode)
    this.editor.setContent(item.content || '');
    this.editor.setReadOnly(true);

    // Update status bar
    const docInfo = document.getElementById('doc-info');
    if (docInfo) {
      docInfo.textContent = `${item.source} | ${item.title} (read-only)`;
    }

    // Set flag to prevent loadDocument from overwriting
    this.isViewingKnowledgeItem = true;

    // Update tab if exists or create temp tab
    const tabTitle = `📖 ${item.title}`;
    if (this.tabManager.hasTab(item.id)) {
      this.tabManager.selectTab(item.id);
    } else {
      this.tabManager.openTab(item.id, tabTitle);
    }

    // Load related docs for this knowledge item
    this.sidebar.loadRelatedDocs(item.id);
  }

  private async moveDocumentToWorkspace(docId: string, workspaceId: string): Promise<void> {
    const response = await api.updateDocument(docId, { workspaceId });

    if (this.docSyncService.syncMeta(docId, response, { workspaceId })) {
      this.sidebar.setActiveWorkspace(workspaceId);
      await this.sidebar.refresh();
    }
  }

  /**
   * Switch to a view via pinned tabs - primary navigation method
   */

  private updateAIActionsVisibility(): void {
    this.aiActionsController.updateVisibility(
      this.currentView,
      this.currentDoc !== null,
      this.knowledgeBaseView.getCurrentPreviewDoc() !== null
    );
  }

  private async handleSidebarTabChange(tab: string): Promise<void> {
    // Delegate to ViewManager
    await this.viewManager.switchToView(tab);

    // Notes-specific: Re-render tabs and load active document when returning
    if (tab === 'notes' || tab === 'docs') {
      this.tabManager.render();
      const activeDocId = this.tabManager.getActiveTabId();
      if (activeDocId && this.tabManager.hasTab(activeDocId)) {
        await this.loadDocument(activeDocId);
      }
    }
  }

  private async exportAllDocuments(): Promise<void> {
    const response = await api.listDocuments();
    if (!response.success || !response.data) return;

    const docs = response.data;
    let exported = 0;

    for (const meta of docs) {
      const docResponse = await api.getDocument(meta.id);
      if (docResponse.success && docResponse.data) {
        const doc = docResponse.data;
        const blob = new Blob([doc.content], { type: 'text/markdown' });
        const a = document.createElement('a');
        a.href = URL.createObjectURL(blob);
        a.download = `${doc.meta.title || 'document'}.md`;
        a.click();
        URL.revokeObjectURL(a.href);
        exported++;
      }
    }

    alert(`Exported ${exported} documents`);
  }

  private async toggleKnowledgeForDoc(docId: string, enabled: boolean): Promise<void> {
    try {
      const response = await resourcesApi.update(docId, { isKnowledgeEnabled: enabled });

      if (response.success && response.data) {
        // Update sidebar document
        const doc = await api.getDocument(docId);
        if (doc.success && doc.data) {
          this.sidebar.updateDocument(doc.data.meta);
        }

        // Update current doc if it's the one being toggled
        if (this.currentDoc?.meta.id === docId) {
          this.currentDoc.meta.isKnowledgeEnabled = enabled;
        }
      }
    } catch (error) {
      console.error('Failed to toggle knowledge:', error);
    }
  }

  private setupKnowledgeSearch(): void {
    // Handle knowledge search result selection
    this.knowledgeSearch.setOnResultSelect(async (docId, source, type) => {
      if (type === 'multimedia') {
        // For multimedia, open source URL in new tab
        const response = await fetch(`${API_BASE_URL}/multimedia/${docId}`);
        const data = await response.json();
        if (data.success && data.data?.sourceUrl) {
          window.open(data.data.sourceUrl, '_blank');
        }
      } else if (source === 'notepad') {
        // For notepad documents, load directly
        await this.loadDocument(docId);
      } else {
        // For external sources, show info (could open in new tab or show details)
        console.log(`Selected ${source} document: ${docId}`);
        // TODO: Handle external sources (gdrive, youtube, etc.)
      }
    });

    // Handle Ask button click from search
    this.knowledgeSearch.setOnAskClick((query) => {
      this.knowledgeQA.open(query);
    });

    // Handle Mind Map button click from search
    this.knowledgeSearch.setOnMindMapClick((query) => {
      this.mindMapView.open(query);
    });

    // Handle connections discovery document selection
    this.connectionsDiscovery.setOnDocumentSelect(async (docId, source) => {
      if (source === 'notepad') {
        await this.loadDocument(docId);
      } else {
        // For external sources, log for now
        console.log(`Selected ${source} document from discovery: ${docId}`);
      }
    });

    // Handle Q&A source selection
    this.knowledgeQA.setOnSourceSelect(async (docId, source) => {
      if (source === 'notepad') {
        await this.loadDocument(docId);
      } else {
        console.log(`Selected ${source} document from Q&A: ${docId}`);
      }
    });

    // Handle Mind Map source selection
    this.mindMapView.setOnSourceSelect(async (docId, source) => {
      if (source === 'notepad') {
        await this.loadDocument(docId);
      } else {
        console.log(`Selected ${source} document from Mind Map: ${docId}`);
      }
    });
  }


  // Review Mode - shows panel for reviewing incomplete multimedia items










  // Called when switching documents to update info panel visibility










  // Filter Bar AI Button Handlers









  // Autocomplete methods













  private exportAsHtml(): void {
    if (!this.currentDoc) {
      alert('No hay documento abierto para exportar');
      return;
    }

    const content = this.editor.getContent();
    const title = this.currentDoc.meta.title || 'documento';

    try {
      const htmlContent = marked.parse(content) as string;
      const fullHtml = `<!DOCTYPE html>
<html lang="es">
<head>
  <meta charset="UTF-8">
  <meta name="viewport" content="width=device-width, initial-scale=1.0">
  <title>${title}</title>
  <style>
    body {
      font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;
      max-width: 800px;
      margin: 0 auto;
      padding: 40px 20px;
      line-height: 1.6;
      color: #333;
    }
    h1, h2, h3 { margin-top: 1.5em; }
    h1 { border-bottom: 1px solid #eee; padding-bottom: 0.3em; }
    h2 { border-bottom: 1px solid #eee; padding-bottom: 0.3em; }
    code { background: #f4f4f4; padding: 2px 6px; border-radius: 4px; }
    pre { background: #f4f4f4; padding: 16px; border-radius: 8px; overflow-x: auto; }
    pre code { background: none; padding: 0; }
    blockquote { border-left: 4px solid #ddd; margin: 1em 0; padding-left: 16px; color: #666; }
    table { border-collapse: collapse; width: 100%; }
    th, td { border: 1px solid #ddd; padding: 8px 12px; text-align: left; }
    th { background: #f4f4f4; }
    a { color: #0066cc; }
    img { max-width: 100%; }
  </style>
</head>
<body>
${htmlContent}
</body>
</html>`;

      // Descargar
      const blob = new Blob([fullHtml], { type: 'text/html;charset=utf-8' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `${title}.html`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
    } catch (e) {
      alert('Error al exportar el documento');
      console.error(e);
    }
  }

  private async toggleFavorite(docId: string, isFavorite: boolean): Promise<void> {
    const response = await api.updateDocument(docId, { isFavorite });

    this.docSyncService.syncWithVersion(docId, response, {
      onSuccess: () => this.updateFavoriteButton()
    });
  }

  private updateFavoriteButton(): void {
    const btn = document.getElementById('btn-bookmark');
    if (!btn) return;

    const icon = btn.querySelector('.material-symbols-outlined') as HTMLElement | null;
    if (!icon) return;

    if (this.currentDoc?.meta.isFavorite) {
      icon.textContent = 'star';
      icon.style.color = '#fbbf24';
      btn.classList.add('active');
      btn.title = 'Remove from favorites';
    } else {
      icon.textContent = 'star_outline';
      icon.style.color = '';
      btn.classList.remove('active');
      btn.title = 'Add to favorites';
    }
  }

  private async moveDocumentToFolder(docId: string, folderId: string | null): Promise<void> {
    const response = await api.updateDocument(docId, { folderId });
    await this.docSyncService.sync(docId, response);
  }

  private async toggleArchive(docId: string, isArchived: boolean): Promise<void> {
    const response = await api.updateDocument(docId, { isArchived });

    const synced = await this.docSyncService.sync(docId, response);

    // Si se archiva y es el documento actual, cerrar el tab
    if (synced && isArchived && this.tabManager.hasTab(docId)) {
      this.tabManager.closeTab(docId, true);
    }
  }

  private async createNewFolder(): Promise<void> {
    const name = prompt('Nombre de la carpeta:');
    if (!name || !name.trim()) return;

    const response = await api.createFolder(name.trim());

    if (response.success && response.data) {
      this.sidebar.addFolder(response.data);
    } else {
      alert('Error al crear la carpeta');
    }
  }



  private exportCurrentDocument(): void {
    if (!this.currentDoc) {
      alert('No document open');
      return;
    }

    const blob = new Blob([this.currentDoc.content], { type: 'text/markdown' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `${this.currentDoc.meta.title || 'document'}.md`;
    a.click();
    URL.revokeObjectURL(url);
  }

  private showNotification(message: string, type: 'success' | 'error' | 'info' = 'success'): void {
    const colors = {
      success: '#10b981',
      error: '#ef4444',
      info: '#3b82f6'
    };

    const notification = document.createElement('div');
    notification.style.cssText = `
      position: fixed;
      bottom: 20px;
      right: 20px;
      padding: 12px 20px;
      background: ${colors[type]};
      color: white;
      border-radius: 8px;
      font-size: 13px;
      box-shadow: 0 4px 12px rgba(0,0,0,0.15);
      z-index: 3000;
      animation: slideInNotif 0.3s ease;
    `;
    notification.textContent = message;

    const style = document.createElement('style');
    style.textContent = `
      @keyframes slideInNotif {
        from { transform: translateX(100%); opacity: 0; }
        to { transform: translateX(0); opacity: 1; }
      }
    `;
    notification.appendChild(style);

    document.body.appendChild(notification);

    setTimeout(() => {
      notification.style.opacity = '0';
      notification.style.transform = 'translateX(100%)';
      notification.style.transition = 'all 0.3s ease';
      setTimeout(() => notification.remove(), 300);
    }, 3000);
  }

  // ═══════════════════════════════════════════════════════════════
  // BACK NAVIGATION
  // ═══════════════════════════════════════════════════════════════







}

// Initialize app
new App();
